import { useLocation } from 'react-router-dom';
import { CoreTypography } from '@youscience/core';
import { majorsDescriptionMap } from '@common-types';

import { CollegeLocation } from '@interfaces/location';

import useFavoriteMajorsStore from '@store/FavoriteMajorsStore';
import useUserPreferencesStore from '@store/UserPreferencesStore';

import { MajorSurveyModalProps, withMajorSurveyModal } from '@HOCs/withMajorSurveyModal';

import { getMajorDetailsPath, SCHOOLS_PATH } from '@constants/ROUTES';

import { OVERALL_FIT_DATA } from '@constants/career';

import { EllipsisTypography } from '@components/EllipsisTypography';

import { getFromScholarshipLocation, getFromSchoolLocation } from '@utils/breadcrumbs';

import HeartIcon from '@images/heart.svg?react';
import FilledHeartIcon from '@images/heart-filled.svg?react';
import ChevronRight from '@images/chevron-right.svg?react';

import {
  sxStyles,
  StyledAreaProgramCardsWrap,
  StyledActionsContainer,
  StyledMoreLink,
  StyledFitChip,
} from './AreaProgramCard.styles';
import ActionButton from '@components/ActionButton';

interface AreaProgramCardProps extends MajorSurveyModalProps {
  programCipCode: number;
  componentId?: string;
  overallFit?: number;
}

export const AreaProgramCard = (props: AreaProgramCardProps) => {
  const { componentId, programCipCode, overallFit, onOpenSurvey } = props;

  const location: CollegeLocation = useLocation();

  const { cipCodes: favoriteMajorsCipCodes } = useUserPreferencesStore((state) => state.majorFavorites);
  const toggleFavoriteMajors = useFavoriteMajorsStore((state) => state.toggleFavoriteMajors);

  const addToFavoritesButtonId = componentId ? `${componentId}-add-to-favorites-button` : 'add-to-favorites-button';

  const cameFromSchool = getFromSchoolLocation(location.pathname);
  const cameFromScholarship = getFromScholarshipLocation(location.pathname);
  const rootLocation = location.pathname.split('/')[1];
  const cameFrom = SCHOOLS_PATH.includes(rootLocation) ? cameFromSchool : cameFromScholarship;

  const handleOpenSurvey = () => {
    onOpenSurvey?.(programCipCode.toString());
  };

  const isFavoriteMajor = !!favoriteMajorsCipCodes?.find(
    (favoriteCipCode: string) => favoriteCipCode === programCipCode.toString(),
  );

  const handleAddToFavorites = () => {
    if (!isFavoriteMajor) {
      handleOpenSurvey();
    }

    void toggleFavoriteMajors(programCipCode.toString());
  };

  const fitData = overallFit ? OVERALL_FIT_DATA[overallFit] : undefined;

  return (
    <StyledAreaProgramCardsWrap>
      {fitData && (
        <StyledFitChip data-testid="programFitChip" $backgroundColor={fitData.color}>
          <CoreTypography variant="caption">{fitData.title}</CoreTypography>
        </StyledFitChip>
      )}

      <EllipsisTypography stringCount={3} sx={sxStyles.programTitle} component="div" variant="h5">
        {majorsDescriptionMap.get(programCipCode.toString())?.cipTitle}
      </EllipsisTypography>

      <StyledActionsContainer>
        <ActionButton
          id={addToFavoritesButtonId}
          sx={sxStyles.favorite}
          variant="text"
          type="button"
          onClick={handleAddToFavorites}
        >
          {isFavoriteMajor ? <FilledHeartIcon data-testid="filled" /> : <HeartIcon data-testid="notFilled" />}
        </ActionButton>

        <StyledMoreLink to={getMajorDetailsPath(programCipCode.toString())} state={cameFrom}>
          More
          <ChevronRight />
        </StyledMoreLink>
      </StyledActionsContainer>
    </StyledAreaProgramCardsWrap>
  );
};

export const AreaProgramCardWithModal = withMajorSurveyModal(AreaProgramCard);
